// src/lib/coverage/parseInventory.ts
// ============================================================
// INVENTORY WORKBOOK PARSER — detect which template was dropped, return typed rows
//
// Four templates, detected by SHEET NAME first and HEADER ROW second, so a workbook whose
// sheet was renamed still parses and a workbook with the right sheet name but the wrong
// columns is rejected rather than silently mis-read.
//
//   Staffing_Capacity     -> D1  staffing and capacity rows
//   Board_Priorities      -> D2  board priority rows
//   Data_Asset_Inventory  -> D3  data asset and platform rows
//   Governance_Charter    -> D4  governance and compliance control areas
//
// ─────────────────────────────────────────────────────────────────────────────
// THE REAL WORKBOOKS ARE NOT BARE GRIDS, and this parser reads the real ones.
//
// The shipped artifacts in demo-assets/templates/ carry three title and preamble rows
// ABOVE the header, and one or more totals and explanatory rows BELOW the data. An earlier
// version of this file assumed row 0 was the header and every row after it was data, which
// read a title as a header, found no columns, and rejected all three files.
//
// So: the header row is FOUND, not assumed, and non-data rows are dropped on the way out.
//   · findHeaderRow scans the first rows for one that satisfies a template's required columns
//   · a totals row (first cell Total / Average / Sum / …) is dropped
//   · a prose row (a single populated cell) is dropped
//
// Dropping the totals row matters more than it looks. D1's Total row repeats 14 FTE and
// 2.1 unallocated; counting it would double both. The ratio survives, so measure 46 would
// still read 1.6 and nothing would look wrong, while the inventory reported 28 FTE.
// ─────────────────────────────────────────────────────────────────────────────
//
// PARSING NEVER THROWS AT THE UI. Every failure path returns ok:false with a note, the same
// contract prototype/utils/initiativesImport already uses, because an error state on a drop
// zone during a live demo is worse than a quiet fallback.
//
// Uses the xlsx (SheetJS) package already in the dependency list. Do not add a second one.
//
// Mock layer only. No backend, no fetch. Reads an ArrayBuffer the caller already has.
// ============================================================

import * as XLSX from 'xlsx';

export type InventoryTemplate =
  | 'staffing'
  | 'boardPriorities'
  | 'dataAssets'
  | 'governanceCharter';

/** Canonical sheet names, per template. */
export const TEMPLATE_SHEET_NAMES: Readonly<Record<InventoryTemplate, string>> = {
  staffing: 'Staffing_Capacity',
  boardPriorities: 'Board_Priorities',
  dataAssets: 'Data_Asset_Inventory',
  governanceCharter: 'Governance_Charter',
};

// ---- row shapes ------------------------------------------------------------

export interface StaffingRow {
  team: string;
  skills: string;
  totalFte: number;
  allocatedFte: number;
  unallocatedFte: number;
}

export interface BoardPriorityRow {
  priority: string;
  /** May be empty. An entirely empty theme column triggers keyword inference downstream. */
  theme: string;
  /** Verbatim funding cell: Yes, Partially, No, or empty. */
  funded: string;
}

export interface DataAssetRow {
  asset: string;
  platform: string;
  standard: string;
  /** Verbatim availability cell: Yes, Partially, No, or empty. */
  availability: string;
  /** Self-rated quality, 1 to 5. NaN when the cell was not a number. */
  quality: number;
  /** Historical depth in years. NaN when the cell was not a number. */
  historicalYears: number;
  refreshCadence: string;
}

export interface GovernanceRow {
  /** The control area. THE join to a measure, matched on its text. */
  domain: string;
  policy: string;
  owner: string;
  /** Self-rated maturity, already on the 1 to 5 scale. NaN when not a number. */
  maturity: number;
  lastReviewed: string;
  dataDomains: string;
}

export type InventoryRows =
  | { template: 'staffing'; rows: StaffingRow[] }
  | { template: 'boardPriorities'; rows: BoardPriorityRow[] }
  | { template: 'dataAssets'; rows: DataAssetRow[] }
  | { template: 'governanceCharter'; rows: GovernanceRow[] };

export type ParseInventoryResult =
  | ({ ok: true; sheetName: string; note?: string } & InventoryRows)
  | { ok: false; note: string };

// ---- header matching -------------------------------------------------------

/** Normalised header key: case, punctuation and spacing folded away. */
function normHeader(s: unknown): string {
  return String(s ?? '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Column aliases per template.
 *
 * The FIRST entry of each list is the header the SHIPPED workbook in demo-assets/templates
 * actually uses, verbatim and normalised. The rest are tolerated variants, kept so a client
 * who retypes a header still parses. When a shipped header changes, change the first entry
 * and leave the variants alone.
 */
const COLUMNS: Record<InventoryTemplate, Record<string, string[]>> = {
  staffing: {
    team: ['team or role group', 'team', 'team name', 'group', 'function'],
    skills: ['key skill areas', 'skills', 'skill areas', 'capabilities'],
    totalFte: ['total fte', 'fte', 'headcount fte'],
    allocatedFte: [
      'percent allocated to active projects',
      'allocated fte',
      'committed fte',
      'assigned fte',
    ],
    unallocatedFte: [
      'unallocated capacity fte',
      'unallocated capacity',
      'unallocated fte',
      'available fte',
      'uncommitted fte',
    ],
  },
  boardPriorities: {
    priority: ['priority', 'board priority', 'initiative', 'description'],
    theme: ['primary theme', 'theme', 'category', 'pillar'],
    funded: ['funded this fy', 'funded', 'funding', 'funded in plan', 'budgeted'],
  },
  dataAssets: {
    asset: ['platform or data domain', 'data asset', 'asset', 'domain', 'data domain'],
    platform: ['vendor or product', 'platform vendor', 'platform', 'vendor', 'system', 'source system'],
    standard: ['standards support', 'standard', 'standards', 'data standard', 'format'],
    availability: [
      'available to the analytics team',
      'availability',
      'available',
      'accessible',
    ],
    quality: [
      'self rated quality 1 to 5',
      'self rated quality',
      'quality 1 5',
      'quality',
      'data quality',
      'quality rating',
    ],
    historicalYears: ['historical depth years', 'historical depth', 'history years', 'years of history'],
    refreshCadence: ['refresh cadence', 'cadence', 'refresh', 'latency', 'frequency'],
  },
  governanceCharter: {
    domain: ['domain or control area', 'control area', 'domain', 'area'],
    policy: ['policy or framework in place', 'policy', 'framework in place', 'framework'],
    owner: ['owner', 'accountable owner'],
    maturity: ['maturity 1 to 5', 'maturity', 'maturity rating'],
    lastReviewed: ['last reviewed', 'last review', 'reviewed'],
    dataDomains: ['data domains it applies to', 'data domains', 'applies to', 'scope'],
  },
};

/** Which columns must be present for a template to be considered a match. */
const REQUIRED: Record<InventoryTemplate, string[]> = {
  staffing: ['team', 'totalFte', 'unallocatedFte'],
  boardPriorities: ['priority'],
  dataAssets: ['asset', 'quality'],
  governanceCharter: ['domain', 'maturity'],
};

/**
 * field -> column index, or -1.
 *
 * Two passes, EXACT before PREFIX, and a column is consumed once claimed. Both details
 * matter: without exact-first, an early field's loose prefix alias steals a column that a
 * later field matches exactly ("fte" would take "Unallocated capacity (FTE)" before
 * "Total FTE" was considered). Without consumption, two fields resolve to one column and
 * the same cell is read as two different things.
 */
function resolveColumns(
  template: InventoryTemplate,
  headers: string[],
): Record<string, number> {
  const normalised = headers.map(normHeader);
  const spec = COLUMNS[template];
  const fields = Object.keys(spec);
  const out: Record<string, number> = {};
  const taken = new Set<number>();

  fields.forEach((f) => {
    out[f] = -1;
  });

  const claim = (field: string, predicate: (header: string) => boolean) => {
    if (out[field] !== -1) return;
    const found = normalised.findIndex((h, i) => h !== '' && !taken.has(i) && predicate(h));
    if (found !== -1) {
      out[field] = found;
      taken.add(found);
    }
  };

  // Pass 1 — exact.
  fields.forEach((field) => claim(field, (h) => spec[field].includes(h)));
  // Pass 2 — prefix, so "Quality (1 to 5) rating" still resolves.
  fields.forEach((field) => claim(field, (h) => spec[field].some((a) => h.startsWith(a))));

  return out;
}

function headersSatisfy(template: InventoryTemplate, headers: string[]): boolean {
  const cols = resolveColumns(template, headers);
  return REQUIRED[template].every((f) => cols[f] !== -1);
}

/** How far down a sheet to look for the header row. The shipped files use row 3. */
const MAX_HEADER_SCAN_ROWS = 12;

/**
 * Index of the header row for a template, or -1.
 *
 * FOUND, not assumed. The shipped workbooks put a title and two preamble lines above the
 * header, so row 0 is a sentence rather than a set of column names.
 */
function findHeaderRow(template: InventoryTemplate, matrix: unknown[][]): number {
  const limit = Math.min(matrix.length, MAX_HEADER_SCAN_ROWS);
  for (let i = 0; i < limit; i += 1) {
    if (headersSatisfy(template, matrix[i].map(String))) return i;
  }
  return -1;
}

/** First-cell labels that mark a totals row rather than a data row. */
const TOTALS_LABEL = /^(total|totals|subtotal|average|mean|sum|count)\b/i;

/**
 * Rows that are not data: totals lines, and the explanatory prose the shipped workbooks
 * carry under the table (a single populated cell spanning the sheet).
 */
function isDataRow(row: unknown[]): boolean {
  const cells = row.map((c) => String(c ?? '').trim());
  const populated = cells.filter((c) => c !== '').length;
  if (populated < 2) return false; // prose footer, or an empty line
  if (TOTALS_LABEL.test(cells.find((c) => c !== '') ?? '')) return false;
  return true;
}

// ---- cell readers ----------------------------------------------------------

const text = (row: unknown[], i: number): string => (i === -1 ? '' : String(row[i] ?? '').trim());

function num(row: unknown[], i: number): number {
  if (i === -1) return NaN;
  const raw = row[i];
  if (typeof raw === 'number') return raw;
  // Strip anything that is not part of a decimal number, so "3.5 FTE" still reads.
  const cleaned = String(raw ?? '').replace(/[^0-9.\-]/g, '');
  return cleaned === '' ? NaN : Number(cleaned);
}

// ---- detection + parse -----------------------------------------------------

interface SheetMatrix {
  sheetName: string;
  matrix: unknown[][];
}

/** Every sheet in the workbook as a header-plus-rows matrix. */
function sheetsOf(workbook: XLSX.WorkBook): SheetMatrix[] {
  return workbook.SheetNames.map((sheetName) => ({
    sheetName,
    matrix: XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[sheetName], {
      header: 1,
      blankrows: false,
      defval: '',
    }) as unknown as unknown[][],
  })).filter((s) => s.matrix.length > 0);
}

/**
 * Pick the sheet and template. Sheet name wins; header shape is the fallback and also the
 * confirmation, so a sheet named Board_Priorities carrying data asset columns is rejected.
 */
export function detectTemplate(
  workbook: XLSX.WorkBook,
): { template: InventoryTemplate; sheet: SheetMatrix; headerRow: number } | null {
  const sheets = sheetsOf(workbook);
  if (sheets.length === 0) return null;

  const templates = Object.keys(TEMPLATE_SHEET_NAMES) as InventoryTemplate[];

  // Pass 1 — sheet name AND header shape agree.
  for (const template of templates) {
    const wanted = normHeader(TEMPLATE_SHEET_NAMES[template]);
    const sheet = sheets.find((s) => normHeader(s.sheetName) === wanted);
    if (sheet) {
      const headerRow = findHeaderRow(template, sheet.matrix);
      if (headerRow !== -1) return { template, sheet, headerRow };
    }
  }

  // Pass 2 — header shape alone, for a workbook whose sheet was renamed.
  for (const template of templates) {
    for (const sheet of sheets) {
      const headerRow = findHeaderRow(template, sheet.matrix);
      if (headerRow !== -1) return { template, sheet, headerRow };
    }
  }

  return null;
}

/** Parse an already-read workbook. Never throws. */
export function parseWorkbook(workbook: XLSX.WorkBook): ParseInventoryResult {
  try {
    const hit = detectTemplate(workbook);
    if (!hit) {
      return { ok: false, note: 'This workbook does not match a known inventory template' };
    }

    const { template, sheet, headerRow } = hit;
    const headers = sheet.matrix[headerRow].map(String);
    const cols = resolveColumns(template, headers);
    // Everything under the header, minus totals lines and the explanatory prose rows the
    // shipped workbooks carry beneath the table.
    const body = sheet.matrix.slice(headerRow + 1).filter(isDataRow);

    if (body.length === 0) return { ok: false, note: 'No data rows found' };

    if (template === 'staffing') {
      const rows: StaffingRow[] = body
        .map((r) => ({
          team: text(r, cols.team),
          skills: text(r, cols.skills),
          totalFte: num(r, cols.totalFte),
          allocatedFte: num(r, cols.allocatedFte),
          unallocatedFte: num(r, cols.unallocatedFte),
        }))
        .filter((r) => r.team !== '' || Number.isFinite(r.totalFte));
      return rows.length === 0
        ? { ok: false, note: 'No staffing rows found' }
        : { ok: true, sheetName: sheet.sheetName, template, rows };
    }

    if (template === 'boardPriorities') {
      const rows: BoardPriorityRow[] = body
        .map((r) => ({
          priority: text(r, cols.priority),
          theme: text(r, cols.theme),
          funded: text(r, cols.funded),
        }))
        .filter((r) => r.priority !== '');
      return rows.length === 0
        ? { ok: false, note: 'No board priorities found' }
        : { ok: true, sheetName: sheet.sheetName, template, rows };
    }

    if (template === 'governanceCharter') {
      const rows: GovernanceRow[] = body
        .map((r) => ({
          domain: text(r, cols.domain),
          policy: text(r, cols.policy),
          owner: text(r, cols.owner),
          maturity: num(r, cols.maturity),
          lastReviewed: text(r, cols.lastReviewed),
          dataDomains: text(r, cols.dataDomains),
        }))
        .filter((r) => r.domain !== '');
      return rows.length === 0
        ? { ok: false, note: 'No control areas found' }
        : { ok: true, sheetName: sheet.sheetName, template, rows };
    }

    const rows: DataAssetRow[] = body
      .map((r) => ({
        asset: text(r, cols.asset),
        platform: text(r, cols.platform),
        standard: text(r, cols.standard),
        availability: text(r, cols.availability),
        quality: num(r, cols.quality),
        historicalYears: num(r, cols.historicalYears),
        refreshCadence: text(r, cols.refreshCadence),
      }))
      .filter((r) => r.asset !== '');
    return rows.length === 0
      ? { ok: false, note: 'No data assets found' }
      : { ok: true, sheetName: sheet.sheetName, template: 'dataAssets', rows };
  } catch {
    return { ok: false, note: 'Could not read the workbook' };
  }
}

/** Parse a dropped file. Never throws. */
export async function parseInventoryFile(file: File): Promise<ParseInventoryResult> {
  try {
    const buffer = await file.arrayBuffer();
    return parseWorkbook(XLSX.read(buffer, { type: 'array' }));
  } catch {
    return { ok: false, note: 'Could not read the file' };
  }
}
